package com.eladfinish.bible.contest.ui.components.toolbar

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import androidx.lifecycle.compose.LocalLifecycleOwner
import androidx.navigation.NavController
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarAppIconButtonConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarBackButtonConfig
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarNavigationMode
import com.eladfinish.bible.contest.ui.components.toolbar.host.AnyToolbarConfiguration

interface ToolbarConfigurator {
    fun setConfiguration(config: AnyToolbarConfiguration?, owner: Any? = null)
    fun updateConfiguration(update: AnyToolbarConfiguration.() -> AnyToolbarConfiguration)

    /**
     * Applies [update] on top of every configuration [owner] sets, until the returned function
     * is called. Unlike [updateConfiguration], a later [setConfiguration] does not undo it.
     */
    fun addUpdate(
        owner: Any?,
        update: AnyToolbarConfiguration.() -> AnyToolbarConfiguration,
    ): () -> Unit
}

val LocalToolbarConfigurator: ProvidableCompositionLocal<ToolbarConfigurator?> =
    compositionLocalOf { null }

@Composable
fun SetToolbarConfiguration(
    configuration: AnyToolbarConfiguration?,
    vararg keys: Any?,
) {
    val configurator = LocalToolbarConfigurator.current ?: return
    val owner = LocalLifecycleOwner.current
    LaunchedEffect(keys = if (keys.isNotEmpty()) keys else arrayOf(configuration)) {
        configurator.setConfiguration(configuration, owner)
    }
}

@Composable
fun <TId : Enum<TId>> UpdateToolbarConfiguration(
    update: ToolbarConfiguration<TId>.() -> ToolbarConfiguration<TId>,
    vararg keys: Any?,
) {
    val configurator = LocalToolbarConfigurator.current ?: return
    val owner = LocalLifecycleOwner.current

    // Registered, not applied once: the screen's SetToolbarConfiguration may run again after this
    // (on web it does when a toolbar icon finishes loading) and would otherwise drop the update.
    DisposableEffect(keys = arrayOf(owner, *keys)) {
        val removeUpdate = configurator.addUpdate(owner) {
            @Suppress("UNCHECKED_CAST")
            val typedConfiguration = this as ToolbarConfiguration<TId>

            typedConfiguration.update()
        }
        onDispose { removeUpdate() }
    }
}

fun ToolbarNavigationMode.specifyWithNavController(
    navController: NavController,
    onAppIconClick: () -> Unit,
    onBackClick: () -> Unit,
    enforceBackButton: Boolean = false,
): ToolbarNavigationMode {
    if (this !is ToolbarNavigationMode.Unspecified) return this

    val isStartDestination = navController.previousBackStackEntry == null
    if (isStartDestination) {
        return ToolbarNavigationMode.NavigationButton(ToolbarAppIconButtonConfig(onAppIconClick))
    }

    if (!isStartDestination || enforceBackButton) {
        return ToolbarNavigationMode.NavigationButton(ToolbarBackButtonConfig(onBackClick))
    }

    return this
}
